import { makeStyles } from '@material-ui/core/styles';
import { pbdqColors, pbdqFont, pbdqMaxWidth, pbdqRadius, pbdqCardShadow } from './pbdqTokens';
import { sdsColors } from './solutionsDesignSystem';

// Shared classes reused across every Powered by DQ marketing page: the nav
// bar, the three button styles, tags/chips, cards and the floating CTA —
// mirrors the DQ Pro workspace/dashboard's own design system (flat pill
// buttons, hairline-bordered white cards, uppercase eyebrow labels) rather
// than the original source mockup's bespoke marketing-site look.
const useSharedStyles = makeStyles(() => ({
    page: {
        background: pbdqColors.bg,
        minHeight: '100vh',
        fontFamily: pbdqFont,
        color: pbdqColors.text,
    },
    container: {
        maxWidth: pbdqMaxWidth,
        margin: '0 auto',
        padding: 0,
    },
    divider: {
        height: 1,
        border: 0,
        margin: 0,
        background: pbdqColors.divider,
    },

    // The product's uppercase micro-label above a heading (PerformanceSummary
    // .page.jsx's "eyebrow" pattern: 11px/700/uppercase/0.6px tracking).
    eyebrow: {
        display: 'block',
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: pbdqColors.accent700,
    },
    // A small pill "kicker" every section title sits above — a tinted badge
    // (dot + eyebrow label) rather than bare uppercase text floating on its
    // own, so each section opens with the same deliberate, branded beat.
    eyebrowChip: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        padding: '7px 16px 7px 12px',
        marginBottom: 18,
        background: pbdqColors.accent200,
        borderRadius: pbdqRadius.pill,
    },
    eyebrowChipDot: {
        width: 6,
        height: 6,
        flexShrink: 0,
        borderRadius: '50%',
        background: pbdqColors.accent,
    },

    // The product's card: white surface, hairline border, whisper shadow —
    // border-led rather than shadow-led. Use with an explicit padding/radius
    // override where a page needs something other than the defaults.
    card: {
        background: pbdqColors.surface,
        border: `1px solid ${pbdqColors.divider}`,
        borderRadius: pbdqRadius.md,
        boxShadow: pbdqCardShadow,
    },

    // A KPI/stat tile: small uppercase label, big 800-weight number, with the
    // product's signature 3px accent rule pinned across the top edge. The
    // rule reads a CSS var so a page can vary its color per card (falls back
    // to the default brand orange when unset).
    statCard: {
        position: 'relative',
        background: pbdqColors.surface,
        border: `1px solid ${pbdqColors.divider}`,
        borderRadius: pbdqRadius.md,
        boxShadow: pbdqCardShadow,
        padding: '20px 22px',
        overflow: 'hidden',
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `var(--pbdqStatAccent, ${pbdqColors.accent})`,
        },
    },
    statValue: {
        fontFamily: pbdqFont,
        fontWeight: 800,
        fontSize: 30,
        lineHeight: 1.1,
        color: pbdqColors.text,
        margin: 0,
    },
    statLabel: {
        fontSize: 12.5,
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        color: pbdqColors.grey,
        margin: '8px 0 0',
    },

    // The standard page hero — every page below Home uses this same shape:
    // eyebrow + big heading + lead + CTAs on the left, and (where the page
    // has a natural list to show — steps, categories, plan tiers) a matching
    // info panel of small cards on the right, same as Home's Define/Measure/
    // Build/Validate/Benchmark panel. Pass `heroNoPanel` on the section when
    // a page has nothing suitable to put in the second column.
    hero: {
        padding: '96px 0 80px',
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 7fr) minmax(0, 5fr)',
        gap: '42px clamp(28px, 4vw, 72px)',
        alignItems: 'center',
    },
    heroNoPanel: {
        gridTemplateColumns: 'minmax(0, 9fr) minmax(0, 3fr)',
    },
    heroTitle: {
        fontFamily: pbdqFont,
        fontWeight: 800,
        fontSize: 'clamp(34px, 4vw, 54px)',
        lineHeight: 1.1,
        letterSpacing: '-0.02em',
        margin: '18px 0 0',
    },
    heroLead: {
        fontSize: 16,
        lineHeight: '27px',
        maxWidth: 530,
        margin: '24px 0 0',
        color: pbdqColors.grey,
        '& p': { margin: 0 },
        '& p + p': { marginTop: 14 },
    },
    heroCtas: {
        display: 'flex',
        gap: 12,
        flexWrap: 'wrap',
        alignItems: 'center',
        marginTop: 30,
    },
    heroPanel: {
        position: 'relative',
        background: pbdqColors.canvas,
        border: `1px solid ${pbdqColors.divider}`,
        borderRadius: pbdqRadius.lg,
        padding: 18,
        display: 'grid',
        gap: 10,
        alignContent: 'start',
        overflow: 'hidden',
        // A soft brand-color glow behind the panel — depth without a heavy
        // shadow, kept subtle enough not to compete with the cards on top.
        '&::before': {
            content: '""',
            position: 'absolute',
            top: -60,
            right: -60,
            width: 200,
            height: 200,
            borderRadius: '50%',
            background: `radial-gradient(circle, ${pbdqColors.accent200} 0%, transparent 72%)`,
            pointerEvents: 'none',
        },
    },
    heroPanelRow: {
        position: 'relative',
        display: 'grid',
        gridTemplateColumns: '44px 1fr',
        gap: 16,
        alignItems: 'center',
        background: pbdqColors.surface,
        border: `1px solid ${pbdqColors.divider}`,
        borderRadius: pbdqRadius.md,
        boxShadow: pbdqCardShadow,
        padding: 16,
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        '&:hover': {
            transform: 'translateY(-2px)',
            borderColor: pbdqColors.accent,
            boxShadow: '0 10px 24px rgba(17,24,39,0.1)',
        },
    },
    heroPanelBar: {
        width: 46,
        height: 46,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: pbdqFont,
        fontWeight: 800,
        fontSize: 16,
        color: '#fff',
        flexShrink: 0,
    },
    // A small in-panel list-item label — not a content "title" in the 3-tier
    // scale sense, so it stays its own compact size instead of pbdqTitleSize.
    heroPanelTitle: {
        fontFamily: pbdqFont,
        fontWeight: 700,
        fontSize: 17,
        letterSpacing: '-0.005em',
        margin: 0,
    },
    heroPanelDesc: {
        fontSize: 14.5,
        lineHeight: '21px',
        margin: '4px 0 0',
        color: pbdqColors.grey,
    },

    navHeader: {
        position: 'sticky',
        top: 0,
        zIndex: 40,
        background: 'rgba(255, 255, 255, 0.92)',
        backdropFilter: 'saturate(180%) blur(12px)',
        WebkitBackdropFilter: 'saturate(180%) blur(12px)',
        borderBottom: '1px solid #E9ECF0',
        width: '100%',
    },
    nav: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: '0 clamp(20px, 4vw, 56px)',
        height: 76,
        display: 'flex',
        alignItems: 'center',
        gap: 'clamp(16px, 2vw, 32px)',
        fontSize: 15,
        color: '#1B2430',
        fontFamily: pbdqFont,
        boxSizing: 'border-box',
        '& a': {
            textDecoration: 'none',
            transition: 'color 120ms',
        },
    },
    navBrand: {
        fontWeight: 700,
        fontSize: 17,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 16,
        letterSpacing: '-0.01em',
    },
    navLogoLink: {
        textDecoration: 'none',
        color: 'inherit',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
    },
    navLogoText: {
        fontSize: 12,
        fontWeight: 700,
        lineHeight: 1.05,
        textAlign: 'right',
        color: '#1B2430',
        fontFamily: pbdqFont,
    },
    navLogoImg: {
        height: 38,
        width: 'auto',
        display: 'block',
    },
    navLogo: {
        display: 'block',
        height: 38,
        width: 'auto',
        objectFit: 'contain',
    },
    navSlogan: {
        fontSize: 12.5,
        lineHeight: 1.35,
        fontWeight: 400,
        letterSpacing: '0.01em',
        textTransform: 'none',
        color: '#6B7280',
        borderLeft: '1px solid #E1E4E9',
        paddingLeft: 16,
        whiteSpace: 'nowrap',
        fontFamily: pbdqFont,
        '@media (max-width:1100px)': {
            display: 'none',
        },
    },
    navLinks: {
        display: 'flex',
        alignItems: 'center',
        gap: 'clamp(18px, 2.4vw, 36px)',
        margin: '0 auto',
        fontSize: 15,
        fontWeight: 500,
        '& a': {
            display: 'inline-flex',
            alignItems: 'center',
            padding: '26px 0 24px',
            borderBottom: '2px solid transparent',
            whiteSpace: 'nowrap',
            fontSize: 15,
            fontWeight: 500,
            color: '#1B2430',
            fontFamily: pbdqFont,
        },
        '& a:hover': {
            color: sdsColors.accent,
        },
    },
    navUtility: {
        display: 'flex',
        alignItems: 'center',
        gap: 'clamp(10px, 1.4vw, 20px)',
        marginLeft: 'auto',
    },
    navLogin: {
        display: 'inline-flex',
        alignItems: 'center',
        fontSize: 15,
        fontWeight: 500,
        color: '#1B2430',
        whiteSpace: 'nowrap',
        fontFamily: pbdqFont,
        '&:hover': {
            color: sdsColors.accent,
        },
    },
    navGetStarted: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
        height: 46,
        padding: '0 20px',
        borderRadius: 999,
        background: '#1B2430',
        color: '#FFFFFF !important',
        fontSize: 15,
        fontWeight: 600,
        whiteSpace: 'nowrap',
        fontFamily: pbdqFont,
        transition: 'background 200ms ease, transform 200ms ease',
        '&:hover': {
            background: '#2A3A4C',
            color: '#FFFFFF !important',
            textDecoration: 'none',
        },
        '&:active': {
            transform: 'scale(0.98)',
        },
    },

    btn: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: 8,
        height: 44,
        padding: '0 22px',
        borderRadius: pbdqRadius.pill,
        fontFamily: pbdqFont,
        fontSize: 18,
        fontWeight: 700,
        lineHeight: 1,
        cursor: 'pointer',
        border: '1px solid transparent',
        boxShadow: 'none',
        transition: 'background 120ms, color 120ms, border-color 120ms',
        whiteSpace: 'nowrap',
        textDecoration: 'none',
        // The app's global bootstrap-css-only stylesheet defines a plain
        // `a:hover { color: #0056b3; text-decoration: underline }`, which
        // out-specifies a single class selector — without reasserting these
        // on our own `:hover`, every link-based button flashes Bootstrap's
        // blue on hover instead of keeping its own color.
        '&:hover': { textDecoration: 'none' },
    },
    btnPrimary: {
        background: pbdqColors.accent,
        color: '#fff',
        '&:hover': { background: pbdqColors.accent600, color: '#fff' },
    },
    btnSecondary: {
        background: pbdqColors.surface,
        color: pbdqColors.accent,
        borderColor: pbdqColors.accent,
        '&:hover': { background: pbdqColors.accent200, color: pbdqColors.accent },
    },
    btnGhost: {
        background: 'transparent',
        color: pbdqColors.accent,
        borderColor: pbdqColors.divider,
        '&:hover': { background: pbdqColors.accent200, color: pbdqColors.accent },
    },
    btnGhostOnDark: {
        color: pbdqColors.bg,
        borderColor: pbdqColors.bg,
        '&:hover': { color: pbdqColors.bg },
    },

    tag: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 12px',
        borderRadius: pbdqRadius.pill,
        fontFamily: pbdqFont,
        fontSize: 12,
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        lineHeight: 1.5,
        border: '1px solid transparent',
        whiteSpace: 'nowrap',
        alignSelf: 'flex-start',
    },
    tagAccent: {
        background: pbdqColors.accent200,
        color: pbdqColors.accent700,
    },
    tagNeutral: {
        background: pbdqColors.neutral100,
        color: pbdqColors.ink2,
    },
    tagOutline: {
        background: 'transparent',
        borderColor: pbdqColors.divider,
        color: pbdqColors.grey,
    },
    tagSuccess: {
        background: pbdqColors.successTint,
        color: pbdqColors.successDeep,
    },

    dqFloat: {
        position: 'fixed',
        right: 24,
        bottom: 24,
        zIndex: 50,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 8,
        background: '#ED6628',
        color: '#FFFFFF',
        border: 0,
        cursor: 'pointer',
        borderRadius: 999,
        padding: '12px 24px',
        fontFamily: pbdqFont,
        fontSize: 15,
        fontWeight: 700,
        textDecoration: 'none',
        boxShadow: '0 8px 24px rgba(237, 102, 40, 0.35)',
        transition: 'background 180ms ease, transform 180ms ease',
        '&:hover': {
            background: '#D5581D',
            color: '#FFFFFF',
            transform: 'translateY(-1px)',
        },
        '&:active': {
            transform: 'scale(0.98)',
        },
    },

    footer: {
        background: '#0F1C27',
        color: '#FFFFFF',
        fontFamily: pbdqFont,
        WebkitFontSmoothing: 'antialiased',
        '& a': {
            color: 'rgba(255, 255, 255, 0.85) !important',
            textDecoration: 'none',
            transition: 'color 120ms ease',
        },
        '& a:hover': {
            color: '#FFFFFF !important',
            textDecoration: 'none',
        },
    },
    footerInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(40px, 5vw, 64px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    footerGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 200px), 1fr))',
        gap: '36px clamp(24px, 3vw, 56px)',
    },
    footerColTitle: {
        fontSize: 12,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: 'rgba(255, 255, 255, 0.55)',
        fontWeight: 700,
        fontFamily: pbdqFont,
    },
    footerCol: {
        display: 'grid',
        gap: 10,
        alignContent: 'start',
        fontSize: 13.5,
        fontFamily: pbdqFont,
    },
    footerLink: {
        fontSize: 13.5,
        fontFamily: pbdqFont,
    },
    footerBlurb: {
        fontSize: 13.5,
        lineHeight: 1.65,
        margin: '14px 0 0',
        maxWidth: '38ch',
        color: 'rgba(255, 255, 255, 0.68)',
        fontFamily: pbdqFont,
    },
    footerLegal: {
        fontSize: 12.5,
        lineHeight: 1.6,
        margin: '32px 0 0',
        paddingTop: 20,
        borderTop: '1px solid rgba(255, 255, 255, 0.14)',
        color: 'rgba(255, 255, 255, 0.55)',
        fontFamily: pbdqFont,
    },
    footerLogo: {
        display: 'block',
        height: 96,
        width: 'auto',
        marginLeft: -8,
        objectFit: 'contain',
    },
}));

export default useSharedStyles;
